<template>
  <div class="container px-0" v-if="$gate.isAdmin()">
    <!-- Toolbar simplu (doar vizualizare) -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Fișiere – toate categoriile</h5>
      <div class="d-flex gap-2">
        <!-- Filtre rapide pe categorie -->
        <div class="btn-group">
          <button v-for="opt in categoryOptions" :key="opt.value" type="button" class="btn btn-sm"
            :class="activeCategories.has(opt.value) ? 'btn-primary' : 'btn-light'" @click="toggleCategory(opt.value)">
            {{ opt.label }}
          </button>
        </div>
      </div>
    </div>

    <!-- Card filtre (perioadă + căutare + erori/avertizări) -->
    <div class="card mb-4">
      <div class="card-header">
        <div class="d-flex flex-wrap gap-2 justify-content-between align-items-center">
          <div class="fw-semibold">Filtrare</div>
        </div>
      </div>
      <div class="card-body">
        <form @submit.prevent="applyFilters">
          <div class="row g-3 align-items-end mb-2">
            <div class="col-12 col-md-3">
              <label class="form-label">Data de la</label>
              <div class="position-relative">
                <input ref="dateFromRef" v-model="dateFrom" type="date" class="form-control pe-5" :min="minDate"
                  :max="dateTo || maxDate" />
                <button type="button" class="date-picker-btn btn btn-outline-secondary btn-sm"
                  @click="openNativePicker('from')" aria-label="Deschide calendar">📅</button>
              </div>
            </div>
            <div class="col-12 col-md-3">
              <label class="form-label">Data până la</label>
              <div class="position-relative">
                <input ref="dateToRef" v-model="dateTo" type="date" class="form-control pe-5" :min="dateFrom || minDate"
                  :max="maxDate" />
                <button type="button" class="date-picker-btn btn btn-outline-secondary btn-sm"
                  @click="openNativePicker('to')" aria-label="Deschide calendar">📅</button>
              </div>
            </div>
            <div class="col-12 col-md-6">
              <label class="form-label">Caută după nume</label>
              <input v-model.trim="filesFilters.q" type="text" class="form-control"
                placeholder="ex: luna, contract, extras..." />
            </div>
          </div>

          <div class="row g-3 align-items-end">
            <div class="col-12 col-md-4 d-flex align-items-center">
              <div class="form-check me-4">
                <input class="form-check-input" type="checkbox" id="filterErrors" v-model="filesFilters.onlyErrors">
                <label class="form-check-label" for="filterErrors">Cu erori</label>
              </div>
              <div class="form-check">
                <input class="form-check-input" type="checkbox" id="filterWarnings" v-model="filesFilters.onlyWarnings">
                <label class="form-check-label" for="filterWarnings">Cu avertizări</label>
              </div>
            </div>
            <div class="col-12 col-md-8 d-flex gap-2 justify-content-md-end">
              <button type="submit" class="btn btn-primary" :disabled="!hasActiveFilters">Aplică</button>
              <button type="button" class="btn btn-light-secondary fw-bold" @click="resetFilters"
                :disabled="!hasActiveFilters">
                <i class="fa-solid fa-rotate-left me-1"></i> Resetare
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- Grid pe luni (combinat) -->
    <div class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <div class="fw-semibold">Fișiere încărcate (toate)</div>
        <small class="text-muted">{{ totalCount }} înregistrări</small>
      </div>
      <div class="card-body">
        <div v-if="loadingFiles" class="p-4 text-center">
          <div class="spinner-border" role="status"></div>
        </div>

        <div v-else>
          <div v-if="!groupedFiles.length" class="p-4 text-center text-muted">
            Nu s-au găsit fișiere după filtrele curente.
          </div>

          <div v-else class="d-flex flex-column gap-4">
            <div v-for="grp in groupedFiles" :key="grp.key">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <h6 class="mb-0">{{ grp.label }}</h6>
                <small class="text-muted">{{ grp.items.length }} fișier{{ grp.items.length === 1 ? '' : 'e' }}</small>
              </div>

              <div class="thumb-grid">
                <div v-for="it in grp.items" :key="`${it.__kind}-${it.id}`" class="thumb-card">
                  <div class="thumb-media">
                    <img :src="serverThumbSrc(it)" :alt="it.original_name" class="img-fluid" loading="lazy" />
                    <!-- status -->
                    <div class="thumb-status" :class="statusClass(it)" :title="statusLabel(it)">
                      <i :class="statusIcon(it)"></i>
                    </div>
                    <!-- badge tip colecție -->
                    <span class="badge bg-secondary position-absolute end-0 top-0 m-2">
                      {{ kindLabel(it.__kind) }}
                    </span>
                  </div>
                  <div class="thumb-body">
                    <div class="thumb-title" :title="it.original_name">{{ it.original_name }}</div>
                    <div class="thumb-meta text-muted">
                      {{ formatDate(it.created_at) }} · {{ humanSize(it.size || 0) }}
                    </div>
                  </div>
                  <div class="thumb-actions">
                    <button class="btn btn-sm btn-light-primary fw-bold" @click="openServerPreview(it)">
                      <i class="fa-solid fa-eye fs-5 me-1"></i> Previzualizare
                    </button>
                    <a class="btn btn-sm btn-light-primary fw-bold" :href="downloadUrl(it)">
                      <i class="fa-solid fa-cloud-arrow-down fs-5 me-1"></i> Descarcare
                    </a>
                  </div>
                </div>
              </div>
            </div> <!-- /group -->
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de previzualizare -->
    <div v-if="serverPreviewItem" class="preview-modal d-flex align-items-center justify-content-center">
      <div class="preview-backdrop" @click="closeServerPreview"></div>
      <div class="preview-content card shadow-lg">
        <div class="card-header d-flex justify-content-between align-items-center">
          <strong class="text-truncate" :title="serverPreviewItem.original_name">{{ serverPreviewItem.original_name
          }}</strong>
          <div class="d-flex gap-2">
            <a class="btn btn-sm btn-light-primary fw-bold" :href="downloadUrl(serverPreviewItem)">
              <i class="fa-solid fa-cloud-arrow-down fs-5 me-1"></i> Descarcare
            </a>
            <button class="btn btn-sm btn-light-secondary fw-bold" @click="closeServerPreview">
              <i class="fa-regular fa-circle-xmark fs-5 me-1"></i> Închide
            </button>
          </div>
        </div>
        <div class="card-body p-0">
          <div class="row g-0 preview-split">
            <!-- Stânga: previzualizare -->
            <div class="col-12 col-lg-7 preview-split-left"
              :class="{ 'zoom-enabled': serverZoomed, 'dragging': isPreviewDragging }" ref="previewLeft"
              @mousedown="onPreviewDragStart">
              <!-- imagine -->
              <div v-if="isImageLike(serverPreviewItem)" class="pannable p-3 h-100"
                :class="{ 'centered': !serverZoomed }">
                <img ref="previewImg" :src="imagePreviewSrc(serverPreviewItem)" alt="preview" class="preview-image"
                  :class="{ 'is-zoomed': serverZoomed }" :style="previewImgStyle"
                  title="Click pentru zoom (1× / 2× / 3×). Ține apăsat și trage pentru a deplasa." draggable="false"
                  @load="onPreviewImgLoaded" @dragstart.prevent @click="toggleServerZoom($event)"
                  @wheel.prevent="onWheelPreview($event)" />
              </div>
              <!-- PDF -->
              <div v-else-if="isPdfLike(serverPreviewItem)" class="pdf-preview h-100">
                <iframe :src="streamUrl(serverPreviewItem)" title="PDF preview"></iframe>
              </div>
              <!-- Office / fallback -->
              <div v-else class="p-4 h-100 d-flex align-items-center justify-content-center text-center">
                <div>
                  <p>Acest tip de fișier nu are previzualizare. Descarcă pentru vizualizare.</p>
                  <a class="btn btn-light-primary fw-bold" :href="downloadUrl(serverPreviewItem)">
                    <i class="fa-solid fa-cloud-arrow-down fs-5 me-1"></i> Descarcare
                  </a>
                </div>
              </div>
            </div>

            <!-- Dreapta: detalii + editare -->
            <div class="col-12 col-lg-5 preview-split-right">
              <form @submit.prevent="updateServerItem" @keydown="filechange_form.onKeydown($event)">
                <div class="p-3 p-lg-4">
                  <div class="text-center mb-3">
                    <button class="btn btn-sm btn-light-primary fw-bold" type="button"
                      @click="openPopupPreview(serverPreviewItem)">
                      <i class="fa-solid fa-up-right-from-square fs-6 me-1"></i> Deschide fișierul
                    </button>
                  </div>

                  <div class="mb-3">
                    <div class="text-muted small">Nume fișier</div>
                    <div class="fw-semibold text-break">{{ serverPreviewItem.original_name || '—' }}</div>
                  </div>

                  <div class="mb-3">
                    <div class="text-muted small d-flex align-items-center justify-content-between">
                      <span>Stare fișier</span>
                      <span>
                        <span class="badge" :class="statusBadgeClass(serverPreviewItem)">
                          <i :class="statusIcon(serverPreviewItem)" class="me-1"></i>
                          {{ statusLabel(serverPreviewItem) }}
                        </span>
                      </span>
                    </div>
                    <multiselect v-model="filechange_form.stare_fisier" :options="stateOptions" label="label"
                      track-by="value" :reduce="opt => opt.value" :searchable="false" :allow-empty="true"
                      :show-labels="false" placeholder="Alege starea" class="mt-2" />
                  </div>

                  <div class="mb-3">
                    <div class="row g-3">
                      <div class="col-12 col-sm-6">
                        <div class="text-muted small">Mărime</div>
                        <div class="fw-semibold">{{ humanSize(serverPreviewItem.size || 0) }}</div>
                      </div>
                      <div class="col-12 col-sm-6">
                        <div class="text-muted small">Data upload</div>
                        <div class="fw-semibold">{{ formatDate(serverPreviewItem.created_at) }}</div>
                      </div>
                    </div>
                  </div>

                  <div class="mb-3">
                    <div class="text-muted small">Lucrat de</div>
                    <div class="fw-semibold">{{ serverPreviewItem.lucrat_de_nume || '—' }}</div>
                  </div>

                  <div class="mb-3">
                    <div class="d-flex align-items-center justify-content-between">
                      <div class="text-muted small">Alte observații</div>
                      <button type="button" class="btn btn-sm btn-light border"
                        @click="editObservatii = !editObservatii"
                        :title="editObservatii ? 'Anulează editarea' : 'Editează'">
                        <i class="fa-solid" :class="editObservatii ? 'fa-xmark' : 'fa-pen-to-square'"></i>
                      </button>
                    </div>
                    <div v-if="!editObservatii" class="text-break">
                      {{ serverPreviewItem.alte_observatii || '—' }}
                    </div>
                    <div v-else class="mt-2">
                      <textarea class="form-control" rows="3" v-model.trim="filechange_form.alte_observatii"
                        placeholder="Adaugă observații..."></textarea>
                    </div>
                  </div>

                  <div>
                    <div class="d-flex align-items-center justify-content-between">
                      <div class="text-muted small">Note</div>
                      <button type="button" class="btn btn-sm btn-light border" @click="editNote = !editNote"
                        :title="editNote ? 'Anulează editarea' : 'Editează'">
                        <i class="fa-solid" :class="editNote ? 'fa-xmark' : 'fa-pen-to-square'"></i>
                      </button>
                    </div>
                    <div v-if="!editNote" class="text-break">
                      {{ serverPreviewItem.note || '—' }}
                    </div>
                    <div v-else class="mt-2">
                      <textarea class="form-control" rows="3" v-model.trim="filechange_form.note"
                        placeholder="Adaugă note..."></textarea>
                    </div>
                  </div>

                  <!-- Mutare fișier -->
                  <div class="mt-4 border-top pt-4">
                    <div class="text-muted small mb-2">Mută fișierul în altă categorie</div>
                    <div class="d-flex gap-2">
                      <multiselect v-model="targetKind"
                        :options="moveOptions.filter(o => o.value !== serverPreviewItem.__kind)" label="label"
                        track-by="value" :reduce="opt => opt.value" :searchable="false" :show-labels="false"
                        placeholder="Alege destinația" class="flex-grow-1" />
                      <button type="button" class="btn btn-light-warning fw-bold" @click="mutaFisier"
                        :disabled="!targetKind || movingFile">
                        <span v-if="movingFile" class="spinner-border spinner-border-sm me-1"></span>
                        Mută
                      </button>
                    </div>
                  </div>

                  <div class="mt-4 d-flex justify-content-end">
                    <button type="submit" class="btn btn-primary fw-bold" :disabled="filechange_form.busy">
                      <span v-if="filechange_form.busy" class="spinner-border spinner-border-sm me-2"
                        role="status"></span>
                      Actualizare
                    </button>
                  </div>
                </div>
              </form>
            </div> <!-- /right -->
          </div>
        </div>
      </div>
    </div> <!-- /modal -->
  </div>
</template>

<script>
import * as pdfjsLib from 'pdfjs-dist/build/pdf.mjs'
import pdfjsWorkerUrl from 'pdfjs-dist/build/pdf.worker.mjs?url'
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.min.css'
import Form from 'vform'

// configurează worker-ul pdf.js
if (pdfjsLib && pdfjsLib.GlobalWorkerOptions) {
  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorkerUrl
}

export default {
  name: 'AdminIncarcariToate',
  components: { Multiselect },
  props: { firmaId: { type: [Number, String], required: true } },

  data() {
    return {
      // filtre
      dateFrom: '',
      dateTo: '',
      filesFilters: { q: '', onlyErrors: false, onlyWarnings: false },

      // stare
      loadingFiles: false,
      filesAll: [],          // lista combinată (iesiri+intrari+extrase+altedocts)
      serverThumbs: {},      // cache: { "<kind>-<id>": { src, pending } }
      firmCreatedAt: null,

      // previzualizare
      serverPreviewItem: null,
      serverZoomed: false,
      serverZoomScale: 1,
      serverZoomLevel: 0,
      zoomScales: [1, 2, 3],
      isPreviewDragging: false,
      previewDragStartX: 0,
      previewDragStartY: 0,
      previewScrollStartLeft: 0,
      previewScrollStartTop: 0,
      suppressNextClick: false,
      movedDuringDrag: false,
      serverImgNaturalW: 0,
      serverImgNaturalH: 0,

      // edit form (vform)
      filechange_form: Form.make({
        id: '',
        stare_fisier: { label: 'În așteptare', value: 0 },
        alte_observatii: '',
        note: '',
      }),
      stateOptions: [
        { value: 0, label: 'În așteptare' },
        { value: 1, label: 'Succes' },
        { value: 2, label: 'Eșec' },
        { value: 3, label: 'Avertizare' },
      ],
      editObservatii: false,
      editNote: false,

      // categorii vizibile
      categoryOptions: [
        { value: 'iesiri', label: 'Ieșiri' },
        { value: 'intrari', label: 'Intrări' },
        { value: 'extrase', label: 'Extrase' },
        { value: 'altedocts', label: 'Alte doc.' },
      ],
      activeCategories: new Set(['iesiri', 'intrari', 'extrase', 'altedocts']),
      targetKind: null,
      movingFile: false,
      moveOptions: [
        { value: 'intrari', label: 'Intrări' },
        { value: 'iesiri', label: 'Ieșiri' },
        { value: 'extrase', label: 'Extrase' },
        { value: 'altedocts', label: 'Alte documente' },
      ],
    }
  },

  computed: {
    hasActiveFilters() {
      return !!(
        this.dateFrom ||
        this.dateTo ||
        (this.filesFilters.q && this.filesFilters.q.trim()) ||
        this.filesFilters.onlyErrors ||
        this.filesFilters.onlyWarnings
      )
    },
    minDate() {
      if (!this.firmCreatedAt) return undefined
      return this.firmCreatedAt.split(' ')[0] // registration date YYYY-MM-DD
    },
    maxDate() {
      const now = new Date()
      const d = new Date(now.getFullYear(), now.getMonth() + 4, 1)
      // last day of that month? User said "4 luni in avans". 
      // Let's just set it to the last day of the 4th month.
      const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0)
      return lastDay.toISOString().split('T')[0]
    },
    groupedFiles() {
      // grupare pe lună (MM_YYYY) din path sau din created_at
      const list = this.filesAll.filter(it => this.activeCategories.has(it.__kind))
      const groups = {}
      for (const it of list) {
        const key = this._extractMonthKey(it) || this._monthKeyFromDate(it.created_at) || '??_????'
        if (!groups[key]) groups[key] = { key, label: this._labelFromMonthKey(key), items: [] }
        groups[key].items.push(it)
      }
      const arr = Object.values(groups)
      // sort desc (an/lună)
      arr.sort((a, b) => {
        const [am, ay] = a.key.split('_').map(Number)
        const [bm, by] = b.key.split('_').map(Number)
        if (ay !== by) return by - ay
        return bm - am
      })
      return arr
    },
    totalCount() { return this.filesAll.length },
    apiBase() { return '/api/administrare/work/fisiere' },

    // Dimensionare imagine în modal (contain vs. zoom)
    previewImgStyle() {
      if (!this.serverZoomed) {
        return { width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }
      }
      const nW = this.serverImgNaturalW || 0
      const scale = this.serverZoomScale || 1
      if (nW > 0) {
        return { width: (nW * scale) + 'px', maxWidth: 'none', height: 'auto' }
      }
      const pct = Math.max(100, Math.min(300, Math.round(scale * 100)))
      return { width: pct + '%', maxWidth: 'none', height: 'auto' }
    },
  },

  mounted() {
    this.fetchFirmData()
    this.fetchAll()
  },

  methods: {
    async fetchFirmData() {
      if (!this.firmaId) return
      try {
        const { data } = await axios.get('/api/administrare/work/fisiere/status_luni', {
          params: { an: new Date().getFullYear(), firma_id: this.firmaId }
        })
        if (data.firm_created_at) {
          this.firmCreatedAt = data.firm_created_at
        }
      } catch (e) {
        console.error('Error fetching firm data:', e)
      }
    },
    // ======= UI helpers =======
    openNativePicker(which) {
      try {
        const el = which === 'from' ? this.$refs.dateFromRef : this.$refs.dateToRef
        if (!el) return
        if (typeof el.showPicker === 'function') el.showPicker()
        else { el.focus(); el.click && el.click() }
      } catch { }
    },
    toggleCategory(kind) {
      if (this.activeCategories.has(kind)) this.activeCategories.delete(kind)
      else this.activeCategories.add(kind)
    },
    applyFilters() {
      if (this.dateFrom && this.dateTo && this.dateFrom > this.dateTo) {
        const t = this.dateFrom; this.dateFrom = this.dateTo; this.dateTo = t
      }
      this.fetchAll()
    },
    resetFilters() {
      if (!this.hasActiveFilters) return
      this.dateFrom = ''
      this.dateTo = ''
      this.filesFilters.q = ''
      this.filesFilters.onlyErrors = false
      this.filesFilters.onlyWarnings = false
      this.fetchAll()
    },

    // ======= Fetch combinat (toate categoriile) =======
    async fetchAll() {
      this.loadingFiles = true
      try {
        const endpoints = [
          { url: `${this.apiBase}/vizualizare_iesiri`, kind: 'iesiri' },
          { url: `${this.apiBase}/vizualizare_intrari`, kind: 'intrari' },
          { url: `${this.apiBase}/vizualizare_extrase`, kind: 'extrase' },
          { url: `${this.apiBase}/vizualizare_altedocts`, kind: 'altedocts' },
        ]
        const params = { firma_id: this.firmaId }
        if (this.filesFilters.q) params.q = this.filesFilters.q
        if (this.dateFrom) params.from = this.dateFrom
        if (this.dateTo) params.to = this.dateTo
        if (this.filesFilters.onlyErrors) params.only_errors = 1
        if (this.filesFilters.onlyWarnings) params.only_warnings = 1

        const results = await Promise.allSettled(
          endpoints.map(ep => axios.get(ep.url, { params, withCredentials: true }))
        )

        let list = []
        results.forEach((res, idx) => {
          const kind = endpoints[idx].kind
          if (res.status === 'fulfilled') {
            const payload = res.value?.data?.data || res.value?.data || []
            list = list.concat(payload.map(x => ({ ...x, __kind: kind })))
          } else {
            console.error(`Eroare la ${endpoints[idx].url}:`, res.reason)
            if (typeof toast !== 'undefined' && toast) {
              toast.fire({
                position: 'top-end',
                icon: 'error',
                title: `Eroare la ${kind}: ${res.reason?.response?.status || ''}`,
                showConfirmButton: false,
                timer: 1500
              })
            }
          }
        })

        // sort desc după create
        list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

        // fallback pentru filtrele de stare
        const { onlyErrors, onlyWarnings } = this.filesFilters
        if (onlyErrors || onlyWarnings) {
          list = list.filter(it => {
            const s = Number(it.stare_fisier)
            const isErr = (s === 2)
            const isWarn = (s === 3)
            if (onlyErrors && onlyWarnings) return isErr || isWarn
            if (onlyErrors) return isErr
            if (onlyWarnings) return isWarn
            return true
          })
        }

        this.filesAll = list

        // generează thumbnails pentru toate
        for (const it of this.filesAll) this.ensureServerThumb(it)
      } catch (e) {
        console.error(e)
        this.filesAll = []
      } finally {
        this.loadingFiles = false
      }
    },

    // ======= Previzualizare / modal =======
    openServerPreview(it) {
      this.serverPreviewItem = it
      this.filechange_form.reset()
      this.filechange_form.id = it.id
      const val = Number(it?.stare_fisier ?? 0)
      const found = this.stateOptions.find(o => o.value === val) || { label: 'În așteptare', value: 0 }
      this.filechange_form.stare_fisier = found
      this.filechange_form.alte_observatii = it?.alte_observatii || ''
      this.filechange_form.note = it?.note || ''
      this.editObservatii = false
      this.editNote = false

      this.serverZoomLevel = 0
      this.serverZoomScale = this.zoomScales[0]
      this.serverZoomed = false
      this.isPreviewDragging = false
      this.suppressNextClick = false
      this.movedDuringDrag = false
      this.serverImgNaturalW = 0
      this.serverImgNaturalH = 0

      this.$nextTick(() => {
        const el = this.$refs.previewLeft
        if (el) { el.scrollTop = 0; el.scrollLeft = 0 }
      })
    },
    closeServerPreview() {
      this.serverPreviewItem = null
      this.serverZoomLevel = 0
      this.serverZoomScale = this.zoomScales[0]
      this.serverZoomed = false
      this.isPreviewDragging = false
      this.detachPreviewDragListeners()
      this.suppressNextClick = false
      this.movedDuringDrag = false
      this.targetKind = null;
      this.movingFile = false;
    },

    // ======= URL helpers =======
    streamUrl(it) { return `${this.apiBase}/stream_${this.kindSegment(it.__kind)}/${it.id}?firma_id=${this.firmaId}` },
    downloadUrl(it) { return `${this.apiBase}/download_${this.kindSegment(it.__kind)}/${it.id}?firma_id=${this.firmaId}` },
    updateUrl(it) { return `${this.apiBase}/update_${this.kindSegment(it.__kind)}/${it.id}` },
    kindSegment(kind) {
      // mapare plural -> singular, conform rutelor din administrare
      switch (kind) {
        case 'iesiri': return 'iesire';
        case 'intrari': return 'intrare';
        case 'extrase': return 'extras';
        case 'altedocts': return 'altedocts'; // deja corect
        default: return kind;
      }
    },
    kindLabel(kind) {
      switch (kind) {
        case 'iesiri': return 'Ieșiri'
        case 'intrari': return 'Intrări'
        case 'extrase': return 'Extrase'
        case 'altedocts': return 'Alte doc.'
        default: return '—'
      }
    },

    // ======= Update pe server =======
    async mutaFisier() {
      if (!this.serverPreviewItem || !this.targetKind) return;

      const targetValue = this.targetKind.value || this.targetKind;
      const targetLabel = this.moveOptions.find(o => o.value === targetValue)?.label || targetValue;

      const result = await Swal.fire({
        title: 'Mutare fișier',
        text: `Sigur vrei să muți acest fișier în categoria "${targetLabel}"?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Da, mută',
        cancelButtonText: 'Anulează'
      });

      if (!result.isConfirmed) return;

      this.movingFile = true;
      try {
        await axios.post('/api/administrare/work/fisiere/muta_fisier', {
          file_id: this.serverPreviewItem.id,
          from_kind: this.serverPreviewItem.__kind,
          to_kind: targetValue,
          firma_id: this.firmaId
        });
        if (typeof toast !== 'undefined' && toast) {
          toast.fire({ icon: 'success', title: 'Fișierul a fost mutat cu succes!', timer: 1500 });
        }
        // Eliminăm din listă locală (sau actualizăm __kind și refresh)
        this.filesAll = this.filesAll.filter(x => !(x.id === this.serverPreviewItem.id && x.__kind === this.serverPreviewItem.__kind));

        this.closeServerPreview();
      } catch (err) {
        console.error(err);
        const msg = err.response?.data?.message || 'Eroare la mutare.';
        if (typeof toast !== 'undefined' && toast) toast.fire({ icon: 'error', title: msg });
      } finally {
        this.movingFile = false;
      }
    },
    async updateServerItem() {
      if (!this.serverPreviewItem) return
      this.$Progress.start()
      try {
        this.filechange_form.firma_id = this.firmaId
        if (this.filechange_form.stare_fisier && typeof this.filechange_form.stare_fisier === 'object') {
          this.filechange_form.stare_fisier = this.filechange_form.stare_fisier.value
        }
        await this.filechange_form.post(this.updateUrl(this.serverPreviewItem))

        if (typeof toast !== 'undefined' && toast) {
          toast.fire({
            position: 'top-end',
            icon: 'success',
            title: 'Fișierul a fost actualizat cu succes!',
            showConfirmButton: false,
            timer: 1200
          })
        }
        this.$Progress.finish()

        // sincronizează local (immutabil)
        const id = this.serverPreviewItem.id
        const kind = this.serverPreviewItem.__kind
        const updated = {
          ...this.serverPreviewItem,
          stare_fisier: Number(this.filechange_form.stare_fisier),
          alte_observatii: this.filechange_form.alte_observatii,
          note: this.filechange_form.note,
        }
        this.filesAll = this.filesAll.map(x =>
          (x.id === id && x.__kind === kind) ? { ...x, ...updated } : x
        )
        this.serverPreviewItem = updated
      } catch (e) {
        console.error(e)
        const errMsg = e?.response?.data?.message || e.message || 'Actualizarea a eșuat.'
        if (typeof toast !== 'undefined' && toast) {
          toast.fire({
            position: 'top-end',
            icon: 'error',
            title: `Eroare: ${errMsg}`,
            showConfirmButton: false,
            timer: 2500
          })
        }
        this.$Progress.fail()
      }
    },

    // ======= Thumbnail-uri & MIME/Ext =======
    serverThumbSrc(it) {
      const key = `${it.__kind}-${it.id}`
      const entry = this.serverThumbs[key]
      if (entry && entry.src) return entry.src
      this.ensureServerThumb(it)
      return this._placeholderSvgDataUrl(it)
    },
    async ensureServerThumb(it) {
      const key = `${it.__kind}-${it.id}`
      const current = this.serverThumbs[key]

      if (current?.pending || current?.src) return

      // pending=true (immutabil)
      this.serverThumbs = {
        ...this.serverThumbs,
        [key]: { ...(current || {}), src: current?.src || '', pending: true }
      }

      let nextSrc = this._placeholderSvgDataUrl(it)

      try {
        if (this.isImageLike(it)) {
          try {
            const imgResp = await axios.get(this.streamUrl(it), { responseType: 'blob', withCredentials: true })
            nextSrc = URL.createObjectURL(imgResp.data)
          } catch (e) {
            // fallback — poate e aceeași origine (cookie-urile există)
            nextSrc = this.streamUrl(it)
          }
        } else if (this.isPdfLike(it)) {
          const resp = await axios.get(this.streamUrl(it), { responseType: 'arraybuffer', withCredentials: true })
          const buf = new Uint8Array(resp.data || [])
          const sig = String.fromCharCode(...buf.slice(0, 5))
          if (sig === '%PDF-') {
            const dataUrl = await this._renderPdfThumbFromArrayBuffer(resp.data)
            if (dataUrl) nextSrc = dataUrl
          }
        } else {
          // alte tipuri => rămâne placeholder
        }
      } catch (e) {
        console.warn('ensureServerThumb failed', key, e)
      } finally {
        const prev = this.serverThumbs[key]?.src
        if (prev && prev.startsWith('blob:') && prev !== nextSrc) {
          try { URL.revokeObjectURL(prev) } catch (_e) { }
        }
        this.serverThumbs = { ...this.serverThumbs, [key]: { src: nextSrc, pending: false } }
      }
    },
    async _renderPdfThumbFromArrayBuffer(arrayBuffer) {
      try {
        const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer, stopAtErrors: true })
        const pdf = await loadingTask.promise
        const page = await pdf.getPage(1)
        const initialViewport = page.getViewport({ scale: 1 })
        const maxEdge = Math.max(initialViewport.width, initialViewport.height)
        const targetEdge = 220
        const scale = Math.max(0.1, Math.min(2.0, targetEdge / maxEdge))
        const viewport = page.getViewport({ scale })
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        if (!ctx) return null
        canvas.width = Math.ceil(viewport.width)
        canvas.height = Math.ceil(viewport.height)
        await page.render({ canvasContext: ctx, viewport }).promise
        return canvas.toDataURL('image/png')
      } catch (e) {
        console.warn('PDF render failed:', e)
        return null
      }
    },
    _placeholderSvgDataUrl(it) {
      const ext = ((it.original_name || '').split('.').pop() || 'file').toUpperCase()
      const title = (it.original_name || '').slice(0, 40)
      const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="420" height="300" viewBox="0 0 420 300">
        <rect width="100%" height="100%" fill="#e9ecef"/>
        <g font-family="system-ui,-apple-system,Segoe UI,Roboto" fill="#495057" text-anchor="middle">
          <text x="210" y="150" font-size="56" font-weight="700">${ext}</text>
          <text x="210" y="190" font-size="14">${title.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</text>
        </g>
      </svg>`
      return 'data:image/svg+xml;utf8,' + encodeURIComponent(svg)
    },

    // MIME helpers + fallback pe extensie
    isImageMime(m) { return /^image\//i.test(m || '') },
    isPdfMime(m) { return /pdf/i.test(m || '') },
    _mimeOf(it) {
      return it?.mime || it?.mimetype || it?.mime_type || ''
    },
    _extOf(it) {
      const name = it?.original_name || it?.stored_name || ''
      const dot = name.lastIndexOf('.')
      return dot !== -1 ? name.slice(dot + 1).toLowerCase() : ''
    },
    isImageLike(it) {
      const m = this._mimeOf(it)
      if (this.isImageMime(m)) return true
      const ext = this._extOf(it)
      return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'tiff', 'svg'].includes(ext)
    },
    isPdfLike(it) {
      const m = this._mimeOf(it)
      if (this.isPdfMime(m)) return true
      const ext = this._extOf(it)
      return ext === 'pdf'
    },
    imagePreviewSrc(it) {
      const key = `${it.__kind}-${it.id}`
      const entry = this.serverThumbs[key]
      if (entry?.src && entry.src.startsWith('blob:')) return entry.src
      return this.streamUrl(it)
    },

    // ======= Status helpers =======
    statusClass(it) {
      const s = Number(it?.stare_fisier ?? 0)
      switch (s) {
        case 1: return 'status-success'
        case 2: return 'status-fail'
        case 3: return 'status-warning'
        default: return 'status-pending'
      }
    },
    statusIcon(it) {
      const s = Number(it?.stare_fisier ?? 0)
      switch (s) {
        case 1: return 'fa-solid fa-check'
        case 2: return 'fa-solid fa-triangle-exclamation'
        case 3: return 'fa-solid fa-circle-exclamation'
        default: return 'fa-solid fa-hourglass-half'
      }
    },
    statusLabel(it) {
      const s = Number(it?.stare_fisier ?? 0)
      switch (s) {
        case 1: return 'Succes'
        case 2: return 'Eșec'
        case 3: return 'Avertizare'
        default: return 'În așteptare'
      }
    },
    statusBadgeClass(it) {
      const s = Number(it?.stare_fisier ?? 0)
      switch (s) {
        case 1: return 'bg-success'
        case 2: return 'bg-danger'
        case 3: return 'bg-warning text-dark'
        default: return 'bg-primary'
      }
    },

    // ======= Zoom & Pan =======
    onPreviewImgLoaded(e) {
      try {
        const img = e?.target
        this.serverImgNaturalW = img?.naturalWidth || 0
        this.serverImgNaturalH = img?.naturalHeight || 0
      } catch { }
    },
    toggleServerZoom(e) {
      if (this.suppressNextClick) return
      const el = this.$refs.previewLeft
      const img = this.$refs.previewImg
      let relX = 0.5, relY = 0.5
      if (img && e && img.getBoundingClientRect) {
        const rect = img.getBoundingClientRect()
        relX = (e.clientX - rect.left) / rect.width
        relY = (e.clientY - rect.top) / rect.height
        relX = Math.max(0, Math.min(1, relX))
        relY = Math.max(0, Math.min(1, relY))
      }
      this.serverZoomLevel = (this.serverZoomLevel + 1) % this.zoomScales.length
      this.serverZoomScale = this.zoomScales[this.serverZoomLevel]
      this.serverZoomed = this.serverZoomLevel > 0
      this.isPreviewDragging = false
      this.$nextTick(() => {
        if (!el || !this.serverZoomed || !this.$refs.previewImg) {
          if (el && !this.serverZoomed) { el.scrollLeft = 0; el.scrollTop = 0 }
          return
        }
        const newImg = this.$refs.previewImg
        const newW = newImg.clientWidth
        const newH = newImg.clientHeight
        const targetLeft = newW * relX - el.clientWidth / 2
        const targetTop = newH * relY - el.clientHeight / 2
        el.scrollLeft = Math.max(0, targetLeft)
        el.scrollTop = Math.max(0, targetTop)
      })
    },
    onWheelPreview(e) {
      if (!this.serverPreviewItem) return
      const el = this.$refs.previewLeft
      const img = this.$refs.previewImg
      let relX = 0.5, relY = 0.5
      if (img && e && img.getBoundingClientRect) {
        const rect = img.getBoundingClientRect()
        relX = (e.clientX - rect.left) / rect.width
        relY = (e.clientY - rect.top) / rect.height
      }
      const dir = e.deltaY > 0 ? -1 : 1
      let nextLevel = this.serverZoomLevel + dir
      nextLevel = Math.max(0, Math.min(this.zoomScales.length - 1, nextLevel))
      if (nextLevel === this.serverZoomLevel) return
      this.serverZoomLevel = nextLevel
      this.serverZoomScale = this.zoomScales[this.serverZoomLevel]
      this.serverZoomed = this.serverZoomLevel > 0
      this.$nextTick(() => {
        if (!el || !this.serverZoomed || !this.$refs.previewImg) {
          if (el && !this.serverZoomed) { el.scrollLeft = 0; el.scrollTop = 0 }
          return
        }
        const newImg = this.$refs.previewImg
        const newW = newImg.clientWidth
        const newH = newImg.clientHeight
        const targetLeft = newW * relX - el.clientWidth / 2
        const targetTop = newH * relY - el.clientHeight / 2
        el.scrollLeft = Math.max(0, targetLeft)
        el.scrollTop = Math.max(0, targetTop)
      })
    },
    onPreviewDragStart(e) {
      if (!this.serverZoomed) return
      if (e.button !== 0) return
      const el = this.$refs.previewLeft
      if (!el) return
      e.preventDefault()
      this.isPreviewDragging = true
      this.movedDuringDrag = false
      this.previewDragStartX = e.clientX
      this.previewDragStartY = e.clientY
      this.previewScrollStartLeft = el.scrollLeft
      this.previewScrollStartTop = el.scrollTop
      window.addEventListener('mousemove', this.onPreviewDragMove, { passive: false })
      window.addEventListener('mouseup', this.onPreviewDragEnd, { passive: false })
    },
    onPreviewDragMove(e) {
      if (!this.isPreviewDragging) return
      const el = this.$refs.previewLeft
      if (!el) return
      e.preventDefault()
      const dx = e.clientX - this.previewDragStartX
      const dy = e.clientY - this.previewDragStartY
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) this.movedDuringDrag = true
      el.scrollLeft = this.previewScrollStartLeft - dx
      el.scrollTop = this.previewScrollStartTop - dy
    },
    onPreviewDragEnd() {
      if (this.isPreviewDragging && this.movedDuringDrag) {
        this.suppressNextClick = true
        setTimeout(() => { this.suppressNextClick = false }, 0)
      }
      this.isPreviewDragging = false
      this.detachPreviewDragListeners()
    },
    detachPreviewDragListeners() {
      window.removeEventListener('mousemove', this.onPreviewDragMove)
      window.removeEventListener('mouseup', this.onPreviewDragEnd)
    },

    // ======= Utils =======
    formatDate(s) {
      if (!s) return '-'
      const d = new Date(s)
      const dd = String(d.getDate()).padStart(2, '0')
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const yyyy = String(d.getFullYear())
      return `${dd}/${mm}/${yyyy}`
    },
    humanSize(bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    _extractMonthKey(it) {
      const p = it.path || ''
      const m = p.match(/\/(\d{2}_\d{4})\//)
      return m ? m[1] : null
    },
    _monthKeyFromDate(iso) {
      if (!iso) return null
      const d = new Date(iso)
      const mm = (d.getMonth() + 1).toString().padStart(2, '0')
      const yyyy = d.getFullYear().toString()
      return `${mm}_${yyyy}`
    },
    _labelFromMonthKey(key) {
      if (!key) return 'Necunoscut'
      const [mm, yyyy] = key.split('_').map(Number)
      const monthNames = ['Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie', 'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie']
      const name = monthNames[(mm - 1) % 12] || mm
      return `${name} ${yyyy}`
    },

    // pop-up separat cu doar fișierul
    openPopupPreview(item) {
      if (!item) return
      const url = this.streamUrl(item)
      const width = 1000, height = 800
      const left = window.screenX + (window.outerWidth - width) / 2
      const top = window.screenY + (window.outerHeight - height) / 2
      window.open(url, 'PopupPreview', `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`)
    },
  }
}
</script>


<style scoped>
/* Small polish for previews */
.ratio {
  width: 100%;
}

/* Modal preview styles */
.preview-modal {
  position: fixed;
  inset: 0;
  z-index: 1050;
}

.preview-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
}

.preview-content {
  position: relative;
  max-width: 1400px;
  width: 95vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

/* Make body the scrollable area */
.preview-content .card-body {
  flex: 1 1 auto;
  overflow: hidden;
  max-height: calc(90vh - 60px);
}

.preview-content img {
  max-width: 100%;
  height: auto;
  display: block;
}

.pdf-preview iframe {
  width: 100%;
  border: 0;
  min-height: 70vh;
}

/* Split modal layout */
.preview-split {
  height: calc(90vh - 60px);
}

.preview-split-left {
  border-right: 1px solid rgba(0, 0, 0, .1);
  background: #fff;
  overflow: hidden;
}

.preview-split-right {
  background: #fff;
  overflow: auto;
  max-height: calc(90vh - 60px);
}

.preview-split-left,
.preview-split-right {
  height: 100%;
}

[data-bs-theme="dark"] .preview-split-left,
[data-bs-theme="dark"] .preview-split-right {
  background: #121212 !important;
  border-color: rgba(255, 255, 255, .12) !important;
}

/* Thumbnails grid for server files */
.thumb-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
}

.thumb-card {
  border: 1px solid rgba(0, 0, 0, .125);
  border-radius: .5rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #fff;
}

.thumb-media {
  position: relative;
  background: #f8f9fa;
  aspect-ratio: 4 / 3;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.thumb-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* Status pill overlay on thumbnail */
.thumb-status {
  position: absolute;
  top: .5rem;
  left: .5rem;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: .9rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, .2);
  border: 1px solid rgba(0, 0, 0, .1);
  z-index: 2;
}

.status-pending {
  background: #0d6efd;
  color: #fff;
}

.status-success {
  background: #198754;
  color: #fff;
}

.status-fail {
  background: #dc3545;
  color: #fff;
}

.status-warning {
  background: #ffc107;
  color: #212529;
  border-color: rgba(0, 0, 0, .2);
}

[data-bs-theme="dark"] .status-warning {
  color: #212529;
}

.thumb-body {
  padding: .5rem .75rem 0;
  font-size: .9rem;
}

.thumb-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 600;
}

.thumb-meta {
  font-size: .8rem;
}

.thumb-actions {
  padding: .5rem .75rem .75rem;
  display: flex;
  gap: .5rem;
}

/* Hide native calendar icon to avoid white-on-white issues */
input[type="date"]::-webkit-calendar-picker-indicator {
  opacity: 0 !important;
}

input[type="date"] {
  color-scheme: light;
}

/* Custom calendar button inside the input */
.date-picker-btn {
  position: absolute;
  right: .5rem;
  top: 50%;
  transform: translateY(-50%);
  line-height: 1;
  padding: .25rem .45rem;
}

/* Dark theme adjustment only for the custom date picker button */
[data-bs-theme="dark"] .date-picker-btn {
  background-color: #1f1f1f !important;
  color: #e9ecef !important;
  border-color: rgba(255, 255, 255, .15) !important;
}

[data-bs-theme="dark"] .date-picker-btn:hover {
  background-color: #262626 !important;
}

@media (prefers-color-scheme: dark) {
  :root:not([data-bs-theme="light"]) .date-picker-btn {
    background-color: #1f1f1f !important;
    color: #e9ecef !important;
    border-color: rgba(255, 255, 255, .15) !important;
  }

  :root:not([data-bs-theme="light"]) .date-picker-btn:hover {
    background-color: #262626 !important;
  }
}

/* === Dark theme fixes for thumbnail area (local only) === */
[data-bs-theme="dark"] .thumb-card {
  background: #121212 !important;
  border-color: rgba(255, 255, 255, .12) !important;
}

[data-bs-theme="dark"] .thumb-media {
  background: #1a1a1a !important;
}

[data-bs-theme="dark"] .thumb-title {
  color: #e9ecef !important;
}

[data-bs-theme="dark"] .thumb-meta {
  color: rgba(233, 236, 239, .6) !important;
}

/* Image zoom in server preview */
.preview-split-left.zoom-enabled {
  overflow: auto;
  cursor: grab;
  user-select: none;
}

.preview-split-left.dragging {
  cursor: grabbing;
}

.preview-image {
  max-width: 100%;
  height: auto;
  display: block;
  transition: width .15s ease;
  cursor: zoom-in;
  -webkit-user-drag: none;
  user-select: none;
}

.preview-image.is-zoomed {
  cursor: grab;
}

.pannable {
  width: 100%;
  height: 100%;
}

.pannable.centered {
  display: flex;
  align-items: center;
  justify-content: center;
}

.pannable.centered .preview-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.preview-split-left.zoom-enabled .pannable {
  display: block;
  min-width: max-content;
  min-height: max-content;
}
</style>